import { z } from 'zod';
import { categoryIdSchema, platformSchema } from '../platform.js';
import { releaseViewSchema } from './catalog.js';

export const moderationItemSchema = z.object({
  release: releaseViewSchema,
  appId: z.string(),
  appName: z.string(),
  developerName: z.string().nullable(),
  submittedAt: z.number().int(),
  checks: z.object({
    sha256: z.enum(['pending', 'passed', 'failed']),
    av: z.enum(['pending', 'clean', 'infected', 'error']),
    authenticode: z.enum(['none', 'valid', 'invalid', 'unverified']),
    peMagic: z.enum(['pending', 'passed', 'failed', 'skipped']),
  }),
});
export type ModerationItem = z.infer<typeof moderationItemSchema>;

export const moderationListResponseSchema = z.object({
  items: z.array(moderationItemSchema),
  nextCursor: z.string().nullable(),
});
export type ModerationListResponse = z.infer<typeof moderationListResponseSchema>;

export const rejectReleaseSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type RejectReleaseInput = z.infer<typeof rejectReleaseSchema>;

export const yankReleaseSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type YankReleaseInput = z.infer<typeof yankReleaseSchema>;

export const importRunSchema = z.object({
  source: z.literal('winget'),
  /** Only run the curated starter list instead of every changed package. */
  curatedOnly: z.boolean().default(true),
  since: z.string().datetime().optional(),
});
export type ImportRunInput = z.infer<typeof importRunSchema>;

export const importStatusSchema = z.object({
  lastRunAt: z.number().int().nullable(),
  lastRunStatus: z.enum(['never', 'running', 'success', 'failed']),
  packagesSeen: z.number().int().nonnegative(),
  packagesImported: z.number().int().nonnegative(),
  hashMismatches: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});
export type ImportStatus = z.infer<typeof importStatusSchema>;

export const statsResponseSchema = z.object({
  windowDays: z.number().int().positive(),
  installs: z.object({
    total: z.number().int().nonnegative(),
    success: z.number().int().nonnegative(),
    failure: z.number().int().nonnegative(),
  }),
  updates: z.object({
    total: z.number().int().nonnegative(),
    success: z.number().int().nonnegative(),
    failure: z.number().int().nonnegative(),
  }),
  /** Null when telemetry is disabled and no reports exist. */
  successRate: z.number().min(0).max(1).nullable(),
  topApps: z.array(
    z.object({
      appId: z.string(),
      name: z.string(),
      installs: z.number().int().nonnegative(),
      failures: z.number().int().nonnegative(),
    }),
  ),
  topErrors: z.array(
    z.object({
      errorCode: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
  pendingModeration: z.number().int().nonnegative(),
  platformBreakdown: z.array(
    z.object({
      platform: platformSchema,
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type StatsResponse = z.infer<typeof statsResponseSchema>;

export const auditEntrySchema = z.object({
  id: z.string(),
  actor: z.string(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string(),
  details: z.string().nullable(),
  createdAt: z.number().int(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditListResponseSchema = z.object({
  items: z.array(auditEntrySchema),
  nextCursor: z.string().nullable(),
});
export type AuditListResponse = z.infer<typeof auditListResponseSchema>;

export const adminAppActionSchema = z.object({
  appId: z.string(),
  action: z.enum(['delist', 'relist']),
  reason: z.string().max(1000).optional(),
});
export type AdminAppActionInput = z.infer<typeof adminAppActionSchema>;

export const categoryOverrideSchema = z.object({
  category: categoryIdSchema,
});
export type CategoryOverrideInput = z.infer<typeof categoryOverrideSchema>;

/**
 * Client (desktop app) release manifest. Published by CI after the installers
 * are built; stored as-is and served by `/v1/client/manifest`.
 */
export const clientReleaseSchema = z.object({
  platform: platformSchema,
  arch: z.enum(['x64', 'arm64']),
  kind: z.enum(['nsis', 'msi']),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
  sizeBytes: z.number().int().positive(),
  downloadUrl: z.string().url(),
  minOs: z.string().regex(/^\d+\.\d+\.\d+$/).nullable(),
});
export type ClientRelease = z.infer<typeof clientReleaseSchema>;

export const clientManifestRequestSchema = z.object({
  manifest: z.object({
    version: z
      .string()
      .regex(/^\d+\.\d+\.\d+([.-][0-9A-Za-z.-]+)?$/, 'version must be semver'),
    publishedAt: z.number().int().nonnegative(),
    releases: z.array(clientReleaseSchema).min(1),
    changelog: z.record(z.enum(['en', 'ru']), z.string()).nullable(),
  }),
  artefacts: z
    .array(
      z.object({
        kind: z.enum(['nsis', 'msi']),
        file: z.string().min(1).max(260),
        sha256: z.string().regex(/^[a-f0-9]{64}$/i),
      }),
    )
    .default([]),
});
export type ClientManifestRequest = z.infer<typeof clientManifestRequestSchema>;